import { useEffect, useRef } from "react";
import { toast } from "sonner";

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "touchmove",
  "scroll",
  "wheel",
];

/**
 * Auto-logout after `idleMs` of no user activity.
 *
 * - Listens to mouse / keyboard / touch / scroll events on the window with
 *   `{ passive: true }` so it doesn't fight the scanner / barcode flows.
 * - Throttles the "I'm still here" reset to once per second to keep the
 *   timer cheap on busy shop-floor tablets.
 * - Fires a `warnMs` heads-up toast before booting the user — they can move
 *   the cursor / tap to abort.
 *
 * Designed for shared tablets: PIN screen is one tap away, no data is lost
 * because everything is server-persisted on submit.
 */
export default function useIdleLogout({
  enabled = true,
  idleMs = 30 * 60 * 1000,   // 30 minutes
  warnMs = 60 * 1000,        // warn 60s before booting
  onLogout,
}) {
  const lastResetRef = useRef(0);
  const idleTimer = useRef(null);
  const warnTimer = useRef(null);
  const onLogoutRef = useRef(null);

  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  useEffect(() => {
    if (!enabled) return undefined;

    const clearTimers = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (warnTimer.current) clearTimeout(warnTimer.current);
    };

    const schedule = () => {
      clearTimers();
      warnTimer.current = setTimeout(() => {
        toast.warning(
          `You'll be signed out in ${Math.round(warnMs / 1000)}s due to inactivity`,
          { duration: warnMs }
        );
      }, Math.max(idleMs - warnMs, 0));
      idleTimer.current = setTimeout(() => {
        onLogoutRef.current?.();
      }, idleMs);
    };

    const reset = () => {
      const now = Date.now();
      // Throttle to 1 reset / second so mousemove doesn't blow up event budget.
      if (now - lastResetRef.current < 1000) return;
      lastResetRef.current = now;
      schedule();
    };

    schedule();
    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, reset, { passive: true })
    );
    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, reset));
    };
  }, [enabled, idleMs, warnMs]);
}
